import { TranscriptionStatus } from '../../domain';
import { DEFAULT_ACTIVE_WORD_COLOR } from './const';

export const EDITOR_TABS = [
  'video-details',
  'video-transcript',
  'video-appearance',
  'content-composer',
  'content-social-post',
  'content-blog-post',
  'content-email',
] as const;

export type EditorTabs = (typeof EDITOR_TABS)[number];

export type ClipSuggestionStatus = 'in_progress' | 'finished' | 'failed';
export type ClipSourceType = 'manual' | 'ai';

// API returns '9_16' for portrait, '16_9' for landscape (null from API = landscape)
export type AspectRatio = '9_16' | '16_9';

export type CaptionPosition = 'top' | 'center' | 'bottom';

export type DraftClip = {
  id: string;
  title: string;
  start_time_ms: number;
  end_time_ms: number;
  source_type: ClipSourceType;
  colorIndex?: number;
  isTemp?: boolean;
  aspect_ratio?: AspectRatio;
  // Frontend-only properties
  isCustomActiveWordColor?: boolean;
  lastUsedBackgroundColor?: string; // Preserves color when blur is toggled
  // API properties (snake_case to match API)
  // background_color: null = blur mode, hex string = solid color
  background_color?: string | null;
  // preset_id: stores the selected preset ID from backend
  preset_id?: string;
  include_captions?: boolean;
  captions_position?: CaptionPosition;
  captions_style?: string;
  captions_color?: string;
  highlight_color?: string;
};

export type ClipSuggestion =
  | null
  | undefined
  | {
      state: ClipSuggestionStatus;
      created_at: string;
    };

export type EditorState = {
  clip_suggestion: ClipSuggestion;
  draft_clips: DraftClip[];
  selectedClip: DraftClip | null;
};

export type SuggestedClipsResponse = {
  draft_clips: DraftClip[];
};

// ── AI Clip Suggestions (new API) ─────────────────────────────────────────────

/**
 * A single AI-suggested clip returned by GET /ai_clip_suggestions/:id.
 * Times are in SECONDS (unlike DraftClip which uses milliseconds).
 * Clips have no individual id — they are positional items within a suggestion.
 */
export type AiClip = {
  title: string;
  start: number; // seconds
  end: number; // seconds
};

export type AiClipSuggestionState =
  | 'initial'
  | 'in_progress'
  | 'finished'
  | 'failed';

/**
 * Response from POST /ai_clip_suggestions (trigger) and
 * GET /ai_clip_suggestions/:id (fetch with clips).
 */
export type AiClipSuggestion = {
  id: number;
  state: AiClipSuggestionState;
  clips?: AiClip[]; // only present when state = 'finished'
  created_at: string;
  updated_at?: string;
};

export type PusherSuggestionUpdate =
  | {
      status: 'finished';
      user_clip_suggestion_id: number;
    }
  | {
      status: 'failed';
    };

export interface TranscriptionStatusUpdate {
  status: TranscriptionStatus;
}

export type SuggestionProcessUpdate =
  | {
      status: 'finished';
      user_clip_suggestion_id: number;
    }
  | {
      status: 'failed';
    };
export type TranscriptResponse = {
  transcript: {
    segments: TranscriptSegment[];
    language: string;
  };
  speakers: Record<string, any>;
};

/**
 * A single word with precise timing from the transcript API.
 * When absent (older transcripts), words are generated from segment.text
 * by equally dividing the segment duration — see generateWordsFromSegment()
 * in the EditTranscript utilities.
 */
export type TranscriptWord = {
  word: string;
  start: number; // seconds
  end: number; // seconds
  score: number; // confidence 0-1
};

export type TranscriptSegment = {
  start: number;
  end: number;
  text: string;
  speaker?: string;
  /** Word-level timing. Present once the backend ships the enriched transcript. */
  words?: TranscriptWord[];
};

export type TranscriptSegmentGroup = {
  speaker: string;
  start: number;
  end: number;
  segments: TranscriptSegment[];
};

export type TranscriptSpeaker = {
  name: string;
};

export type BrandingPreset = {
  id: string;
  isPredefined: boolean;
  isDefault: boolean;
  name: string;
  config: Record<string, string | Record<string, string>>;
};

export type ColorPreset = {
  id: string;
  isPredefined: boolean;
  isDefault: boolean;
  name: string;
  theme: Record<string, string>;
};

// --- Advanced Edit types (aligned with backend create API schema) ---

/** Backend values for aspect ratio. Non-null in FE state. */
export type AdvancedEditAspectRatio = '16_9' | '9_16' | '1_1';

/** Transition between cuts. Backend-compatible shape. */
export type AdvancedEditTransition =
  | { type: 'jump_cut' }
  | { type: 'fade'; duration: number };

/** Single cut in an edit. Field names match backend create payload. */
export type AdvancedEditCut = {
  // Frontend-only ID for stable rendering and local updates. Strip this before API calls.
  id: string;
  recording_id: string;
  start_time_ms: number;
  end_time_ms: number;
  transition: AdvancedEditTransition;
};

/** Captions config. Backend-compatible discriminated union. */
export type AdvancedEditCaptions =
  | { enabled: false }
  | {
      enabled: true;
      highlight_color: string;
      /** Style id in draft (e.g. 'impact-bold'); converted to font family on PUT. */
      captions_style: string;
      captions_position: 'top' | 'center' | 'bottom';
      /** Stored when loaded from API; derived from preset on PUT — not user-editable. */
      captions_color: string;
      preset_id: string;
    };

/** Editable draft payload for an edit. Matches backend create request shape. */
export type AdvancedEditDraft = {
  title: string;
  cuts: AdvancedEditCut[];
  captions: AdvancedEditCaptions;
  aspectRatio: AdvancedEditAspectRatio;
  backgroundColor: string | null;
  /**
   * Selected brand preset (V1 clip `preset_id`). Used for portrait background and
   * the brand carousel when captions are off. Not sent on PUT — API only carries
   * `preset_id` inside `captions` when `enabled: true`.
   */
  presetId?: string;
  /**
   * Transcript word-level corrections. Each entry replaces one word in the
   * transcript display. Sent to the PUT /edits/:id API on every auto-save.
   * Empty array until the word corrections UI is used.
   */
  wordCorrections: WordCorrection[];
};

export type AdvancedEditStatus =
  | 'DRAFT'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED';

/** Full edit entity stored in Redux. */
export type RecordingEditEntity = {
  id: string;
  status: AdvancedEditStatus;
  revision: number;
  createdAt?: string;
  updatedAt?: string;
  isLocalOnly?: boolean;
  draft: AdvancedEditDraft;
};

/** Default aspect ratio when bootstrapping a new edit. */
export const DEFAULT_ADVANCED_EDIT_ASPECT_RATIO: AdvancedEditAspectRatio =
  '16_9';

/** Default captions when bootstrapping. */
export const DEFAULT_ADVANCED_EDIT_CAPTIONS: AdvancedEditCaptions = {
  enabled: false,
};

/** Default values when the user first enables captions on an edit. */
export const DEFAULT_ADVANCED_EDIT_CAPTIONS_ENABLED: Extract<
  AdvancedEditCaptions,
  { enabled: true }
> = {
  enabled: true,
  highlight_color: DEFAULT_ACTIVE_WORD_COLOR,
  captions_style: 'impact-bold',
  captions_position: 'bottom',
  captions_color: '',
  preset_id: '',
};

/** Undo/redo history for an edit (Decision 1: Hybrid Redux + Context). */
export type EditHistory = {
  undo: AdvancedEditDraft[];
  redo: AdvancedEditDraft[];
};

/** Maximum number of undo/redo steps to keep in history. */
export const MAX_HISTORY_SIZE = 50;

/** Persistence state for an edit (tracks auto-save progress). */
export type EditPersistState = 'idle' | 'saving' | 'saved' | 'failed';

/**
 * Why a save failed — used to show the appropriate tooltip message.
 * 'conflict' — 409 from the server (stale revision / export lock).
 *              User must reload to get the current revision.
 * 'generic'  — any other error (network, 5xx, etc.).
 *              User can retry by making another edit.
 */
export type EditSaveFailureReason = 'conflict' | 'generic';

/** Checkpoint info for save recovery (Option 2: Flexible undo with checkpoint). */
export type EditPersistCheckpoint = {
  status: EditPersistState;
  lastSavedVersion: number;
  lastSavedDraft?: AdvancedEditDraft;
  lastSavedAt?: string;
  /** Only set when status === 'failed'. Drives the tooltip error message. */
  failureReason?: EditSaveFailureReason;
};

export type RecordingEditListApiRow = {
  id: string;
  title: string;
  cuts?: Array<{
    start_time_ms: number;
    end_time_ms: number;
  }>;
  aspectRatio?: string | null;
  createdAt?: string;
  /**
   * Export/lifecycle status for this edit.
   * Optional — the list endpoint currently omits it; once the backend starts
   * returning it this field will be populated automatically.
   */
  status?: AdvancedEditStatus;
};

export type AdvancedEditsState = {
  status: 'idle' | 'processing' | 'success' | 'failed';
  /** Load state for the recording edits list (GET .../edits). */
  listStatus: 'idle' | 'loading' | 'success' | 'failed';
  listTotal: number;
  listCursor: string | null;
  edits: RecordingEditListApiRow[];
  /**
   * Full edit documents (draft, cuts, …) keyed by id — for local/new edits and
   * when a detail load has populated them. List rows are not duplicated here.
   */
  editEntitiesById: Record<string, RecordingEditEntity>;
  activeEditId: string | null;
  /** Undo/redo history per edit (Decision 1: Hybrid approach for persistence). */
  historyByEditId: Record<string, EditHistory>;
  /** Auto-save checkpoint per edit (Option 2: Flexible undo with version tracking). */
  persistByEditId: Record<string, EditPersistCheckpoint>;
  /** Version counter per edit (tracks edit count for checkpoint comparison). */
  editVersionByEditId: Record<string, number>;
  /**
   * Per-edit fetch status for the GET-by-ID API call.
   * 'idle'    — not yet requested for this editId
   * 'loading' — request in-flight
   * 'success' — full entity stored in editEntitiesById
   * 'failed'  — request failed; placeholder entity may still be present
   */
  editFetchStatusById: Record<
    string,
    'idle' | 'loading' | 'success' | 'failed'
  >;
};

/** Creates default advanced edits state. */
export function createDefaultAdvancedEditsState(): AdvancedEditsState {
  return {
    status: 'idle',
    listStatus: 'idle',
    listTotal: 0,
    listCursor: null,
    edits: [],
    editEntitiesById: {},
    activeEditId: null,
    historyByEditId: {},
    persistByEditId: {},
    editVersionByEditId: {},
    editFetchStatusById: {},
  };
}

export function editListCutsFromDraft(
  cuts: AdvancedEditCut[],
): NonNullable<RecordingEditListApiRow['cuts']> {
  return cuts.map(({ start_time_ms, end_time_ms }) => ({
    start_time_ms,
    end_time_ms,
  }));
}

export function recordingEditSummaryFromEntity(
  entity: RecordingEditEntity,
): RecordingEditListApiRow {
  return {
    id: entity.id,
    title: entity.draft.title,
    aspectRatio: entity.draft.aspectRatio,
    createdAt: entity.createdAt ?? new Date().toISOString(),
    cuts:
      entity.draft.cuts.length > 0
        ? editListCutsFromDraft(entity.draft.cuts)
        : undefined,
  };
}

/**
 * Query string params for GET …/recordings/:recordingId/edits.
 */
export type GetRecordingEditsQuery = {
  cursor?: string;
  pageSize?: string;
};

export const RECORDING_EDITS_PAGE_SIZE = 5;

export function buildRecordingEditsListSearchParams(
  query: GetRecordingEditsQuery = {},
): string {
  const params = new URLSearchParams();
  params.set('pageSize', query.pageSize ?? String(RECORDING_EDITS_PAGE_SIZE));
  if (query.cursor != null && query.cursor !== '') {
    params.set('cursor', query.cursor);
  }
  return params.toString();
}

export type RecordingEditsListApiResponse = {
  cursor: string | null;
  total: number;
  data: RecordingEditListApiRow[];
};

export type PostRecordingEditRequest = {
  title: string;
  captions:
    | {
        enabled: true;
        highlight_color: string;
        captions_style: string;
        captions_position: 'top' | 'center' | 'bottom';
      }
    | {
        enabled: false;
      };
  cuts: {
    recording_id: string;
    start_time_ms: number;
    end_time_ms: number;
    transition:
      | {
          type: 'jump_cut';
        }
      | {
          type: 'fade';
          duration: number;
        };
  }[];
  aspectRatio?: '16_9' | '9_16' | '1_1';
  backgroundColor?: string;
  /**
   * Always send [] when creating a new edit or when corrections are not yet
   * applicable. The field is required by the server schema — omitting it may
   * cause a 400. Matches the same constraint on PutRecordingEditRequest.
   */
  wordCorrections: WordCorrection[];
};

/**
 * A single word-level correction stored against an edit.
 * The component for creating/editing/displaying corrections is not yet built —
 * for now the application always sends [] to satisfy the server's required field.
 * TODO(EVE-14659): Implement word corrections UI and wire this type fully.
 */
export type WordCorrection = {
  segmentIndex: number;
  wordIndex: number;
  word: string;
};

/**
 * Full-replace request body for PUT /v2/:orgId/recordings/:recordingId/edits/:editId.
 *
 * Key differences vs PostRecordingEditRequest:
 * - `revision` is required — optimistic concurrency token (echo the value
 *   received from the most recent GET or PUT success response).
 * - `aspectRatio` is required — PUT has no server-side default.
 * - `cuts[].id` must NOT be included — `id` is a FE-only stable key;
 *   strip it with `map(({ id: _id, ...rest }) => rest)` before sending.
 * - Omitting `backgroundColor` writes NULL to the DB (full-replace semantics;
 *   no PATCH / partial-update support).
 */
export type PutRecordingEditRequest = {
  title: string;
  cuts: Array<Omit<AdvancedEditCut, 'id'>>;
  captions: AdvancedEditCaptions;
  aspectRatio: AdvancedEditAspectRatio;
  /**
   * Optional — DO NOT send null explicitly.
   * The content-hub PUT schema is `z.string().optional()` (not nullable).
   * Sending null causes a 400 "Expected string, received null".
   * Omitting the field (undefined) tells the server to write NULL to the DB,
   * which is the correct blur-mode representation. Contrast: the clip API accepts
   * `background_color: null` directly; the edit API does not.
   */
  backgroundColor?: string;
  revision: number;
  /**
   * Always send [] until the word corrections feature is implemented.
   * The field is required by the server schema — omitting it may cause a 400.
   * TODO(EVE-14659): Populate from AdvancedEditDraft.wordCorrections once the
   * word corrections UI is built.
   */
  wordCorrections: WordCorrection[];
};

/**
 * Success response from PUT /v2/:orgId/recordings/:recordingId/edits/:editId.
 *
 * Identical shape to the POST (create) response.
 * Notably ABSENT (vs GET-by-ID): `status` and `hasUnexportedChanges` — the
 * server does not join export history on the update path. After a successful
 * PUT, the client can safely infer hasUnexportedChanges = true.
 * The critical field to persist: `revision` (the new N+1 value to use for
 * the next PUT).
 */
export type PutRecordingEditApiResponse = {
  id: string;
  title: string;
  cuts: Array<Omit<AdvancedEditCut, 'id'>>;
  captions: AdvancedEditCaptions;
  aspectRatio: AdvancedEditAspectRatio;
  backgroundColor: string | null;
  revision: number;
  createdAt: string;
  updatedAt: string;
  wordCorrections: WordCorrection[];
};

/**
 * Full response shape from GET /v2/:orgId/recordings/:recordingId/edits/:editId.
 * Unlike the list summary, this includes captions, backgroundColor, revision,
 * status and hasUnexportedChanges — all required for the edit view to function
 * correctly (rendering, save/export guards, optimistic concurrency).
 */
export type GetRecordingEditByIdApiResponse = {
  id: string;
  title: string;
  cuts: Array<{
    recording_id: string;
    start_time_ms: number;
    end_time_ms: number;
    transition: AdvancedEditTransition;
  }>;
  captions: AdvancedEditCaptions;
  aspectRatio: AdvancedEditAspectRatio;
  backgroundColor: string | null;
  revision: number;
  status: AdvancedEditStatus;
  hasUnexportedChanges: boolean;
  createdAt: string;
  updatedAt: string;
  wordCorrections: WordCorrection[];
};

/**
 * Builds a highlight edit from all AI-suggested clips (one cut per clip).
 */
export const buildCreateHighlightEditRequest = (
  recordingId: string,
  aiClips: AiClip[],
  title: string,
): PostRecordingEditRequest => ({
  title,
  captions: { enabled: false },
  cuts: aiClips.map(clip => ({
    recording_id: recordingId,
    start_time_ms: Math.round(clip.start * 1000),
    end_time_ms: Math.round(clip.end * 1000),
    transition: { type: 'jump_cut' },
  })),
  aspectRatio: '16_9',
  wordCorrections: [],
});

/**
 * Builds create-edit API payload from recording context (full recording span).
 */
export const buildCreateEditRequest = (
  recordingId: string,
  durationSeconds: number,
  title: string,
): PostRecordingEditRequest => {
  const endTimeMs = Math.max(0, durationSeconds * 1000);

  return {
    title,
    captions: { enabled: false },
    cuts: [
      {
        recording_id: recordingId,
        start_time_ms: 0,
        end_time_ms: endTimeMs,
        transition: { type: 'jump_cut' },
      },
    ],
    aspectRatio: '16_9',
    wordCorrections: [],
  };
};

/** Title prefix applied to a cloned edit (e.g. "cloned-My edit"). */
export const CLONED_EDIT_TITLE_PREFIX = 'cloned-';

/**
 * Strips one leading `cloned-`, re-prefixes, and de-dupes against
 * `existingTitles` with ` (2)`, ` (3)`, … (case/whitespace-insensitive).
 */
export const buildClonedTitle = (
  sourceTitle: string,
  existingTitles: readonly string[] = [],
): string => {
  const baseName = sourceTitle.startsWith(CLONED_EDIT_TITLE_PREFIX)
    ? sourceTitle.slice(CLONED_EDIT_TITLE_PREFIX.length)
    : sourceTitle;
  const candidate = `${CLONED_EDIT_TITLE_PREFIX}${baseName}`;

  const taken = new Set(existingTitles.map(t => t.trim().toLowerCase()));
  if (!taken.has(candidate.trim().toLowerCase())) {
    return candidate;
  }

  // Bounded loop — limits to 1000 clones to prevent pathological input
  for (let n = 2; n <= 1000; n++) {
    const next = `${candidate} (${n})`;
    if (!taken.has(next.trim().toLowerCase())) {
      return next;
    }
  }
  // Fallback to timestamp suffix if all 1000 suffixes taken (highly unlikely)
  return `${candidate} (${Date.now()})`;
};

/**
 * Builds a create-edit API payload from a full edit entity.
 *
 * Uses the entity's draft directly so that all fields — cuts, captions,
 * aspectRatio, backgroundColor, and wordCorrections — are faithfully cloned.
 * The title receives the "cloned-" prefix and is de-duped against existingTitles.
 */
export const buildCloneEditRequest = (
  entity: RecordingEditEntity,
  recordingId: string,
  existingTitles: readonly string[] = [],
): PostRecordingEditRequest => {
  const { draft } = entity;

  // Map AdvancedEditCut[] → PostRecordingEditRequest['cuts']:
  // strip the FE-only `id` field; keep recording_id, times, and transition.
  const cuts: PostRecordingEditRequest['cuts'] = draft.cuts.map(cut => ({
    recording_id: recordingId,
    start_time_ms: cut.start_time_ms,
    end_time_ms: cut.end_time_ms,
    transition: cut.transition,
  }));

  return {
    title: buildClonedTitle(draft.title, existingTitles),
    captions: draft.captions,
    cuts,
    aspectRatio: draft.aspectRatio ?? DEFAULT_ADVANCED_EDIT_ASPECT_RATIO,
    backgroundColor: draft.backgroundColor ?? undefined,
    wordCorrections: draft.wordCorrections,
  };
};
